import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { hasProperty } from '@fmnts/core';
import { coerceIsoDate, isBefore, isInvalidDate } from '@fmnts/core/chronos';
import { ControlPath } from '../angular-forms.types';

export type DateRangeValidationError = ControlPath[];

const VALIDATOR_ERROR_PROP_NAME = 'date-range';

export interface DateRangeValidatorOptions {
  startControlName?: string;
  endControlName?: string;
  allowEqual?: boolean;
}

/**
 * Type guard that checks if any `ValidationErrors` contain
 * the errors from the {@link dateRangeValidator}.
 *
 * @returns
 * `true`, if the `ValidationErrors` object `e` contains
 * the errors from the {@link dateRangeValidator}.
 */
export const hasDateRangeError = hasProperty(
  VALIDATOR_ERROR_PROP_NAME,
  (u): u is DateRangeValidationError => Array.isArray(u),
);

/**
 * Creates a validation function to check a Date-range.
 * Applied at the FormGroup level.
 */
export function dateRangeValidator(
  options: DateRangeValidatorOptions = { allowEqual: true },
): ValidatorFn {
  const {
    startControlName = 'startDate',
    endControlName = 'endDate',
    allowEqual = true,
  } = options;

  return (control: AbstractControl): ValidationErrors | null => {
    const startControl = control.get(startControlName);
    const endControl = control.get(endControlName);

    const startValue = coerceIsoDate(startControl?.value);
    const endValue = coerceIsoDate(endControl?.value);

    if (isInvalidDate(startValue) || isInvalidDate(endValue)) {
      return null;
    }

    // Check chronological order
    const isBeforeCheck = isBefore(endValue, { exclusive: !allowEqual });
    if (!isBeforeCheck(startValue)) {
      return { endBeforeStart: true };
    }

    return null;
  };
}
